'use client';

import { c as _c } from "react/compiler-runtime";
import 'react-image-crop/dist/ReactCrop.css';
import Image from 'next/image';
import { useRef, useState } from 'react';
import ReactCrop, { centerCrop, type Crop, makeAspectCrop, type PixelCrop } from 'react-image-crop';
import Modal, { ModalContent, ModalFooter, ModalHeader } from '../HOC/Modal';
import Button from './Button';
import { canvasPreview } from './canvasPreview';
import { useDebounceEffect } from './useDebounceEffect';
type Props = {
  aspect?: number;
  afterImageCropHandler: Function;
  src: string;
  closeHandler: () => void;
  circularCrop?: boolean;
};
const CropImage = t0 => {
  const $ = _c(44);
  const {
    aspect,
    afterImageCropHandler,
    src,
    closeHandler,
    circularCrop: t1
  } = t0;
  const circularCrop = t1 === undefined ? false : t1;
  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const [scale] = useState(1);
  const [rotate] = useState(0);
  const [completedCrop, setCompletedCrop] = useState();
  const [crop, setCrop] = useState();
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      width: 100,
      height: 100
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const [imageDimensions, setImageDimensions] = useState(t2);
  let t3;
  if ($[1] !== afterImageCropHandler || $[2] !== closeHandler || $[3] !== completedCrop) {
    t3 = async () => {
      const image = imgRef.current;
      if (!image || !completedCrop) {
        throw new Error("Crop canvas does not exist");
      }
      const scaleX = image.naturalWidth / image.offsetWidth;
      const scaleY = image.naturalHeight / image.offsetHeight;
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        throw new Error("No 2d context");
      }
      canvas.width = completedCrop.width * scaleX;
      canvas.height = completedCrop.height * scaleY;
      ctx.drawImage(image, completedCrop.x * scaleX, completedCrop.y * scaleY, completedCrop.width * scaleX, completedCrop.height * scaleY, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(blob => {
        afterImageCropHandler(blob);
      });
      closeHandler();
    };
    $[1] = afterImageCropHandler;
    $[2] = closeHandler;
    $[3] = completedCrop;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const afterImageCropHandlerFunc = t3;
  const centerAspectCrop = _temp;
  let t4;
  if ($[5] !== aspect) {
    t4 = e => {
      const {
        naturalWidth,
        naturalHeight
      } = e.currentTarget;
      const maxWidth = window.innerWidth * 0.5;
      const maxHeight = window.innerHeight * 0.5;
      let displayWidth;
      let displayHeight;
      const widthRatio = maxWidth / naturalWidth;
      const heightRatio = maxHeight / naturalHeight;
      const scaleRatio = Math.min(1, widthRatio, heightRatio);
      displayWidth = naturalWidth * scaleRatio;
      displayHeight = naturalHeight * scaleRatio;
      setImageDimensions({
        width: displayWidth,
        height: displayHeight
      });
      const initialCrop = centerAspectCrop(displayWidth, displayHeight, aspect || 1);
      setCrop(initialCrop);
      setCompletedCrop({
        x: Math.round(initialCrop.x / 100 * displayWidth),
        y: Math.round(initialCrop.y / 100 * displayHeight),
        width: Math.round(initialCrop.width / 100 * displayWidth),
        height: Math.round(initialCrop.height / 100 * displayHeight),
        unit: "px"
      });
    };
    $[5] = aspect;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const onImageLoad = t4;
  let t5;
  if ($[7] !== completedCrop || $[8] !== rotate || $[9] !== scale) {
    t5 = () => {
      if (completedCrop?.width && completedCrop?.height && imgRef.current && previewCanvasRef.current) {
        canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop, scale, rotate);
      }
    };
    $[7] = completedCrop;
    $[8] = rotate;
    $[9] = scale;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== completedCrop || $[12] !== rotate || $[13] !== scale) {
    t6 = [completedCrop, scale, rotate];
    $[11] = completedCrop;
    $[12] = rotate;
    $[13] = scale;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  useDebounceEffect(t5, 100, t6);
  let t7;
  if ($[15] !== closeHandler) {
    t7 = () => {
      closeHandler();
    };
    $[15] = closeHandler;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <ModalHeader><h2>Crop Image</h2></ModalHeader>;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t10;
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = (_, percentCrop) => setCrop(percentCrop);
    t10 = c => setCompletedCrop(c);
    $[18] = t10;
    $[19] = t9;
  } else {
    t10 = $[18];
    t9 = $[19];
  }
  const t11 = aspect || 1;
  const t12 = `scale(${scale}) rotate(${rotate}deg)`;
  let t13;
  if ($[20] !== t12) {
    t13 = {
      transform: t12
    };
    $[20] = t12;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== imageDimensions.height || $[23] !== imageDimensions.width || $[24] !== onImageLoad || $[25] !== src || $[26] !== t13) {
    t14 = <Image ref={imgRef} alt="Crop me" src={src} width={imageDimensions.width} height={imageDimensions.height} style={t13} onLoad={onImageLoad} />;
    $[22] = imageDimensions.height;
    $[23] = imageDimensions.width;
    $[24] = onImageLoad;
    $[25] = src;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== circularCrop || $[29] !== crop || $[30] !== t11 || $[31] !== t14) {
    t15 = <ModalContent><div className="flex items-center justify-center"><ReactCrop crop={crop} onChange={t9} onComplete={t10} aspect={t11} circularCrop={circularCrop} keepSelection={true}>{t14}</ReactCrop></div></ModalContent>;
    $[28] = circularCrop;
    $[29] = crop;
    $[30] = t11;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== closeHandler) {
    t16 = <Button type="button" text="Cancel" onClick={closeHandler} />;
    $[33] = closeHandler;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== afterImageCropHandlerFunc) {
    t17 = <Button type="button" text="Crop" classNames="w-auto" onClick={afterImageCropHandlerFunc} />;
    $[35] = afterImageCropHandlerFunc;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t16 || $[38] !== t17) {
    t18 = <ModalFooter>{t16}{t17}</ModalFooter>;
    $[37] = t16;
    $[38] = t17;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t15 || $[41] !== t18 || $[42] !== t7) {
    t19 = <Modal closeHandler={t7}>{t8}{t15}{t18}</Modal>;
    $[40] = t15;
    $[41] = t18;
    $[42] = t7;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  return t19;
};
export default CropImage;
function _temp(mediaWidth, mediaHeight, aspect_0) {
  return centerCrop(makeAspectCrop(mediaWidth / mediaHeight > aspect_0 ? {
    unit: "%" as const,
    height: 100
  } : {
    unit: "%" as const,
    width: 100
  }, aspect_0, mediaWidth, mediaHeight), mediaWidth, mediaHeight);
}